import { PRODUCT_CODES, type AuthUser, type ProductCode } from '@gb-ticket/shared-types';
import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';

import { productPathPrefixes } from '@/platform/lib/products';

/** Link target for a mobile context slot. */
export type ProductNavLinkSlot = {
  kind: 'link';
  id: string;
  label: string;
  to: string;
  isVisible?: (user: AuthUser) => boolean;
};

/** Overlay or custom control for a mobile context slot (e.g. Intranet channels sheet). */
export type ProductNavActionSlot = {
  kind: 'action';
  id: string;
  label: string;
  Component: ComponentType;
  isVisible?: (user: AuthUser) => boolean;
};

export type ProductNavSlot = ProductNavLinkSlot | ProductNavActionSlot;

/** One destination in the mobile Menü for a product. */
export type ProductMenuTile = {
  id: string;
  label: string;
  to: string;
  /** Same glyph as the desktop entry. Falls back to the shared path catalogue. */
  icon?: LucideIcon;
  group?: string;
  isVisible?: (user: AuthUser) => boolean;
};

export type ProductNavRegistration = {
  code: ProductCode;
  /** Product-owned left navigation. Hooks stay inside this component. */
  Nav: ComponentType;
  /** Up to three frequent mobile slots. Invisible slots drop out; later ones move left. */
  mobileSlots?: readonly ProductNavSlot[];
  /** Static menu tiles. Prefer `menuTilesFor` when visibility needs the whole nav. */
  menuTiles?: readonly ProductMenuTile[];
  /** Menu tiles for the signed-in person, including Helpdesk groups. */
  menuTilesFor?: (user: AuthUser) => ProductMenuTile[];
  /** Extra tiles that need a hook (Intranet Schnellzugriff). */
  useExtraTiles?: () => ProductMenuTile[];
};

const registrations = new Map<ProductCode, ProductNavRegistration>();

/** Products register at import time; the platform never imports product code for this. */
export function registerProductNav(registration: ProductNavRegistration): void {
  registrations.set(registration.code, registration);
}

export function productNavRegistration(
  code: ProductCode | null | undefined,
): ProductNavRegistration | undefined {
  return code ? registrations.get(code) : undefined;
}

/** Longest matching product path prefix wins. `/portal` belongs to Helpdesk. */
export function activeProductCode(pathname: string): ProductCode | null {
  let best: { code: ProductCode; length: number } | null = null;
  for (const code of PRODUCT_CODES) {
    for (const prefix of productPathPrefixes(code)) {
      if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
        if (!best || prefix.length > best.length) {
          best = { code, length: prefix.length };
        }
      }
    }
  }
  return best?.code ?? null;
}

export function visibleMobileSlots(
  registration: ProductNavRegistration | undefined,
  user: AuthUser | null | undefined,
): ProductNavSlot[] {
  if (!registration?.mobileSlots || !user) {
    return [];
  }
  return registration.mobileSlots.filter((slot) => !slot.isVisible || slot.isVisible(user));
}

export function productMenuTiles(
  registration: ProductNavRegistration | undefined,
  user: AuthUser,
): ProductMenuTile[] {
  if (!registration) {
    return [];
  }
  if (registration.menuTilesFor) {
    return registration.menuTilesFor(user);
  }
  return (registration.menuTiles ?? []).filter((tile) => !tile.isVisible || tile.isVisible(user));
}

export function productMenuPaths(user: AuthUser): Set<string> {
  const paths = new Set<string>();
  for (const code of user.products ?? []) {
    for (const tile of productMenuTiles(registrations.get(code), user)) {
      paths.add(tile.to);
    }
  }
  return paths;
}
